import dayjs from 'dayjs';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { isCornMaturity } from './corn-stage';
import type { StageMessage } from './shared';

/**
 * "MF" (Madurez Fisiológica / physiological maturity) banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring has reached maturity
 * (`phenology === 'maturity'`). It nudges taking crop samples with the grower to estimate the
 * final yield and the likely harvest date.
 *
 * Returns `null` for non-corn/non-advisor users or before maturity.
 */
export const useMaturityStageMessage = ({
    cropMonitoring,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    if (!isValidatedBayerUserType || !isCornMaturity(cropMonitoring)) return null;

    return {
        title: _('Estimá el rendimiento del lote'),
        message: _(
            'Tomá muestras del cultivo con el productor para estimar el rendimiento y conocer la fecha probable de cosecha.'
        ),
        actions: [
            { label: _('Ver cómo tomar muestras'), variant: 'contained', action: 'view-sampling-guide' },
            { label: _('Estimar rinde'), variant: 'outlined', action: 'estimate-yield' },
        ],
    };
};
